import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import "../documents/Documents.css";
import "./Attendance.css";
import { AttendanceGuideButton, AttendanceHelpModal } from "./AttendanceHelp";
import {
  buildAttendanceConfigurationRows,
  fetchCompanyOfficesForAttendance,
  fetchOfficeAttendanceByCompany,
  handleAttendanceApiError,
  saveOfficeAttendanceSettings,
} from "./attendanceApi";
import { Common, WebStorage, WebStorageNames } from "../../../utils";
import { validateAttendanceWorkdayAndGrace } from "./attendanceDateUtils";

/** @param {(key: string, opts?: Record<string, unknown>) => string} t */
const scheduleValidationMessage = (t, v) => {
  if (v.valid) return null;
  switch (v.reason) {
    case "same":
      return t("app.attendance.configuration.validationSameTime");
    case "order":
      return t("app.attendance.configuration.validationStartAfterEnd");
    case "parse":
      return t("app.attendance.configuration.validationInvalidTime");
    case "grace":
      return t("app.attendance.configuration.validationGraceExceedsWindow", {
        span: v.span,
        graceTotal: v.graceTotal,
        graceIn: v.graceIn,
        graceOut: v.graceOut,
      });
    default:
      return t("app.attendance.configuration.validationInvalidTime");
  }
};

const getRoleId = () => {
  const r = WebStorage.getLocalStore(WebStorageNames.RoleId) ?? localStorage.getItem("roleId");
  const n = parseInt(String(r), 10);
  return Number.isFinite(n) ? n : null;
};

const isCompanyAdmin = (rid) => rid === 1 || rid === 2;

const toTimeInput = (v) => {
  if (v == null || v === "") return "09:00";
  const s = String(v);
  const m = s.match(/(\d{1,2}):(\d{2})/);
  if (m) return `${m[1].padStart(2, "0")}:${m[2]}`;
  return "09:00";
};

const AttendanceConfigurationPage = () => {
  const { t } = useTranslation();
  const roleId = getRoleId();
  const allowed = isCompanyAdmin(roleId);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [helpKey, setHelpKey] = useState(null);

  const [workStart, setWorkStart] = useState("09:00");
  const [workEnd, setWorkEnd] = useState("18:00");
  const [graceIn, setGraceIn] = useState(15);
  const [graceOut, setGraceOut] = useState(0);
  const [allowEdit, setAllowEdit] = useState(false);
  const [allowDelete, setAllowDelete] = useState(false);
  const [allowMulti, setAllowMulti] = useState(true);
  const [flexWindow, setFlexWindow] = useState(false);
  const [requireUserLocation, setRequireUserLocation] = useState(false);
  const [allowBiometricCheckOut, setAllowBiometricCheckOut] = useState(true);

  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    try {
      const [settingsRows, offices] = await Promise.all([
        fetchOfficeAttendanceByCompany().catch(() => []),
        fetchCompanyOfficesForAttendance().catch(() => []),
      ]);
      const mapped = buildAttendanceConfigurationRows(offices, settingsRows);
      setList(mapped);
      setSelectedId((prev) => {
        const prevNum = prev != null ? Number(prev) : null;
        if (prevNum != null && mapped.some((x) => Number(x.officeId) === prevNum)) return prevNum;
        return mapped[0]?.officeId != null ? Number(mapped[0].officeId) : null;
      });
    } catch (e) {
      handleAttendanceApiError(e, t("app.attendance.errors.loadSettings"));
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [allowed, t]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = useMemo(
    () => list.find((x) => Number(x.officeId) === Number(selectedId)),
    [list, selectedId]
  );

  useEffect(() => {
    if (!selected) return;
    setWorkStart(toTimeInput(selected.workDayStart));
    setWorkEnd(toTimeInput(selected.workDayEnd));
    setGraceIn(Number(selected.checkInGraceMinutes) || 0);
    setGraceOut(Number(selected.checkOutGraceMinutes) || 0);
    setAllowEdit(!!selected.allowUsersToEditSubmittedEvents);
    setAllowDelete(!!selected.allowUsersToDeleteSubmittedEvents);
    setAllowMulti(selected.allowMultipleCheckInOutPerDay !== false);
    setFlexWindow(!!selected.allowFlexibleWorkingWindow);
    setRequireUserLocation(!!selected.requireUserLocation);
    setAllowBiometricCheckOut(selected.allowBiometricCheckOut !== false);
  }, [selected]);

  const scheduleValidation = useMemo(
    () =>
      validateAttendanceWorkdayAndGrace({
        workStart,
        workEnd,
        checkInGraceMinutes: graceIn,
        checkOutGraceMinutes: graceOut,
        allowFlexibleWorkingWindow: flexWindow,
      }),
    [workStart, workEnd, graceIn, graceOut, flexWindow]
  );

  const scheduleValidationHint = useMemo(
    () => scheduleValidationMessage(t, scheduleValidation),
    [t, scheduleValidation]
  );

  const persist = async () => {
    if (!selected) return;
    const officeId = Number(selected.officeId ?? selectedId);
    if (!Number.isFinite(officeId) || officeId <= 0) {
      Common.showErrorMessage(t("app.attendance.errors.saveSettings"));
      return;
    }
    const v = validateAttendanceWorkdayAndGrace({
      workStart,
      workEnd,
      checkInGraceMinutes: graceIn,
      checkOutGraceMinutes: graceOut,
      allowFlexibleWorkingWindow: flexWindow,
    });
    if (!v.valid) {
      Common.showErrorMessage(scheduleValidationMessage(t, v));
      return;
    }
    setSaving(true);
    try {
      await saveOfficeAttendanceSettings({
        officeId,
        workDayStart: workStart,
        workDayEnd: workEnd,
        checkInGraceMinutes: graceIn,
        checkOutGraceMinutes: graceOut,
        allowFlexibleWorkingWindow: flexWindow,
        allowUsersToEditSubmittedEvents: allowEdit,
        allowUsersToDeleteSubmittedEvents: allowDelete,
        allowMultipleCheckInOutPerDay: allowMulti,
        requireUserLocation,
        allowBiometricCheckOut,
      });
      Common.showSuccessMessage(t("app.attendance.configuration.saveSuccess"));
      await load();
    } catch (e) {
      handleAttendanceApiError(e, t("app.attendance.errors.saveSettings"));
    } finally {
      setSaving(false);
    }
  };

  const scheduleLocked = flexWindow;

  if (!allowed) {
    return (
      <div className="px-3 py-4">
        <p className="text-muted">{t("app.attendance.errors.loadSettings")}</p>
      </div>
    );
  }

  return (
    <div className="px-3 px-md-4 py-4">
      <div className="att-attendance-route">
        <CommonPageHeader
          icon={<i className="bi bi-sliders" />}
          title={t("app.attendance.configuration.title")}
          description={t("app.attendance.configuration.subtitle")}
          rightContent={<AttendanceGuideButton onClick={() => setHelpKey("configuration")} />}
        />

        <div className="att-page">
        <div className="mb-3 small text-muted">
          <span>{t("app.attendance.configuration.breadcrumbSettings")}</span>
          <i className="bi bi-chevron-right mx-1" />
          <span className="text-primary fw-semibold">{t("app.attendance.configuration.breadcrumbRules")}</span>
        </div>

        {loading ? (
          <p>{t("app.common.loading")}</p>
        ) : list.length === 0 ? (
          <div className="alert alert-light border text-muted">
            {t("app.attendance.configuration.emptyOffices")}
          </div>
        ) : (
          <>
            <section className="att-config-office-strip mb-4">
              <div className="att-config-office-strip-head">
                <h2 className="att-config-office-strip-title">{t("app.attendance.configuration.officeSectionTitle")}</h2>
                <p className="att-config-office-strip-hint">{t("app.attendance.configuration.officeSectionHint")}</p>
              </div>
              <div className="att-office-picker" role="tablist" aria-label={t("app.attendance.configuration.officeSectionTitle")}>
                {list.map((o) => {
                  const id = Number(o.officeId);
                  const active = Number(selectedId) === id;
                  return (
                    <button
                      key={String(o.officeId)}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      className={`att-office-card ${active ? "att-is-active" : ""}`}
                      onClick={() => setSelectedId(id)}
                    >
                      <span className="att-office-card-icon" aria-hidden>
                        <i className="bi bi-building" />
                      </span>
                      <span className="att-office-card-name">{o.officeName || `Office ${o.officeId}`}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            <div className="att-config-shell">
              <div className="att-config-grid">
              <section className="att-config-panel att-config-panel--schedule att-config-span-8">
                <div className="att-config-panel-head mb-4">
                  <div className="att-config-icon-badge att-config-icon-badge--primary" aria-hidden>
                    <i className="bi bi-clock-history" />
                  </div>
                  <div className="att-config-panel-head-text">
                    <h3 className="att-config-panel-title">{t("app.attendance.configuration.workingHours")}</h3>
                    <p className="att-config-panel-lead">{t("app.attendance.configuration.workingHoursSub")}</p>
                  </div>
                </div>
                <div className={`row g-4 ${scheduleLocked ? "att-config-fields-muted" : ""}`}>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">{t("app.attendance.configuration.shiftStart")}</label>
                    <input
                      type="time"
                      className="form-control att-input-time att-input-time--padded"
                      value={workStart}
                      onChange={(e) => setWorkStart(e.target.value)}
                      disabled={scheduleLocked}
                    />
                    <p className="small text-muted mt-1 mb-0">{t("app.attendance.configuration.shiftStartHint")}</p>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold">{t("app.attendance.configuration.shiftEnd")}</label>
                    <input
                      type="time"
                      className="form-control att-input-time att-input-time--padded"
                      value={workEnd}
                      onChange={(e) => setWorkEnd(e.target.value)}
                      disabled={scheduleLocked}
                    />
                    <p className="small text-muted mt-1 mb-0">{t("app.attendance.configuration.shiftEndHint")}</p>
                  </div>
                </div>
                {!scheduleLocked && scheduleValidationHint ? (
                  <div className="alert alert-warning border-0 mt-3 mb-0 py-2 px-3 small att-config-validation-alert" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-2" aria-hidden />
                    {scheduleValidationHint}
                  </div>
                ) : null}
                <div className="mt-4 pt-4 att-config-muted-band rounded-3 px-3 py-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div>
                    <h4 className="h6 fw-bold mb-1">{t("app.attendance.configuration.flexWindow")}</h4>
                    <p className="small text-muted mb-0">{t("app.attendance.configuration.flexHint")}</p>
                  </div>
                  <button
                    type="button"
                    className={`att-toggle ${flexWindow ? "att-is-on" : ""}`}
                    aria-pressed={flexWindow}
                    onClick={() => setFlexWindow((v) => !v)}
                    title={t("app.attendance.configuration.flexToggleTitle")}
                  >
                    <span className="att-toggle-thumb" />
                  </button>
                </div>

                <div className="att-config-employee-edits">
                  <p className="att-config-employee-edits-title">{t("app.attendance.configuration.employeeEditsTitle")}</p>
                  <div className="att-config-employee-edits-list">
                    <label className="form-check">
                      <input type="checkbox" className="form-check-input" checked={allowEdit} onChange={(e) => setAllowEdit(e.target.checked)} />
                      <span className="form-check-label">{t("app.attendance.configuration.allowEdit")}</span>
                    </label>
                    <label className="form-check">
                      <input type="checkbox" className="form-check-input" checked={allowDelete} onChange={(e) => setAllowDelete(e.target.checked)} />
                      <span className="form-check-label">{t("app.attendance.configuration.allowDelete")}</span>
                    </label>
                    <label className="form-check">
                      <input type="checkbox" className="form-check-input" checked={allowMulti} onChange={(e) => setAllowMulti(e.target.checked)} />
                      <span className="form-check-label">{t("app.attendance.configuration.allowMultiple")}</span>
                    </label>
                    <label className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={requireUserLocation}
                        onChange={(e) => setRequireUserLocation(e.target.checked)}
                      />
                      <span className="form-check-label">{t("app.attendance.configuration.requireUserLocation")}</span>
                    </label>
                    <p className="small text-muted mb-0 ps-4 ms-1">{t("app.attendance.configuration.requireUserLocationHint")}</p>
                    <label className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={allowBiometricCheckOut}
                        onChange={(e) => setAllowBiometricCheckOut(e.target.checked)}
                      />
                      <span className="form-check-label">{t("app.attendance.configuration.allowBiometricCheckOut")}</span>
                    </label>
                    <p className="small text-muted mb-0 ps-4 ms-1">{t("app.attendance.configuration.allowBiometricCheckOutHint")}</p>
                  </div>
                </div>
              </section>

              <section className={`att-config-panel att-config-panel--grace att-config-span-4 ${scheduleLocked ? "att-config-fields-muted" : ""}`}>
                <div className="att-config-panel-head mb-3">
                  <div className="att-config-icon-badge att-config-icon-badge--success" aria-hidden>
                    <i className="bi bi-stopwatch" />
                  </div>
                  <div className="att-config-panel-head-text">
                    <h3 className="att-config-panel-title">{t("app.attendance.configuration.graceTitle")}</h3>
                    <p className="att-config-panel-lead mb-0">{t("app.attendance.configuration.gracePanelIntro")}</p>
                  </div>
                </div>

                <div className="att-config-grace-block mb-4">
                  <p className="att-config-grace-section-title">{t("app.attendance.configuration.graceCheckInTitle")}</p>
                  <p className="small text-muted mb-3">{t("app.attendance.configuration.graceCheckInIntro")}</p>
                  <div className="bg-white rounded-3 p-3 att-config-grace-card">
                    <div className="d-flex align-items-baseline gap-2 mb-2">
                      <span className="display-6 fw-bold text-primary">{graceIn}</span>
                      <span className="text-muted">{t("app.attendance.configuration.graceMinutes")}</span>
                    </div>
                    <input
                      type="range"
                      className="att-range"
                      min={0}
                      max={60}
                      value={graceIn}
                      onChange={(e) => setGraceIn(Number(e.target.value))}
                      disabled={scheduleLocked}
                      aria-label={t("app.attendance.configuration.graceCheckInTitle")}
                    />
                  </div>
                  <p className="small text-secondary mt-2 mb-0">{t("app.attendance.configuration.graceCheckInHint")}</p>
                </div>

                <div className="att-config-grace-block">
                  <p className="att-config-grace-section-title">{t("app.attendance.configuration.graceCheckOutTitle")}</p>
                  <p className="small text-muted mb-3">{t("app.attendance.configuration.graceCheckOutIntro")}</p>
                  <div className="bg-white rounded-3 p-3 att-config-grace-card">
                    <div className="d-flex align-items-baseline gap-2 mb-2">
                      <span className="display-6 fw-bold text-primary">{graceOut}</span>
                      <span className="text-muted">{t("app.attendance.configuration.graceMinutes")}</span>
                    </div>
                    <input
                      type="range"
                      className="att-range"
                      min={0}
                      max={120}
                      value={graceOut}
                      onChange={(e) => setGraceOut(Number(e.target.value))}
                      disabled={scheduleLocked}
                      aria-label={t("app.attendance.configuration.graceCheckOutTitle")}
                    />
                  </div>
                  <p className="small text-secondary mt-2 mb-0">{t("app.attendance.configuration.graceCheckOutHint")}</p>
                </div>
              </section>
              </div>
            </div>

            <div className="att-action-bar">
              <span className="small text-muted">
                <i className="bi bi-info-circle me-1" />
                {t("app.attendance.configuration.lastUpdated")}
              </span>
              <div className="d-flex gap-2 align-items-center">
                <button type="button" className="att-btn att-btn--ghost" onClick={() => selected && load()} disabled={saving}>
                  {t("app.attendance.configuration.discard")}
                </button>
                <button
                  type="button"
                  className="att-btn att-btn--save-primary"
                  onClick={persist}
                  disabled={saving || !selected || !scheduleValidation.valid}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm text-white" role="status" aria-hidden />
                      {t("app.common.loading")}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check2" aria-hidden />
                      {t("app.attendance.configuration.save")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      </div>
      <AttendanceHelpModal helpKey={helpKey} onClose={() => setHelpKey(null)} />
    </div>
  );
};

export default AttendanceConfigurationPage;
